/**
 * Pure rules evaluation logic for RJ Tracking simulator.
 * Mirrors tracker worker's routing semantics:
 * - Match wildcard-or-equal per field
 * - Group by specificity (desc) → priority (asc) → weighted pool
 * - Schedule off excludes rule from consideration
 */

export interface SimRule {
  country: string | null;
  deviceType: string | null;
  os: string | null;
  source: string | null;
  weight: number;
  priority: number;
  isActive: boolean;
  scheduleActive?: boolean;
  offerId: number;
  id: number; // For UI identification
}

export interface SimInput {
  country: string;
  deviceType: string;
  os: string;
  source?: string;
}

interface SimResult {
  winner: SimRule;
  pool: SimRule[];
}

/**
 * Check if a value is considered "wildcard" (matches anything).
 */
function isWildcard(v: string | null | undefined): boolean {
  const s = (v ?? '').toString().trim().toUpperCase();
  return !s || s === '*' || s === 'GLOBAL' || s === 'ALL' || s === 'ANY';
}

/**
 * Check if a rule matches a given input for a specific field.
 * Wildcard in rule always matches; otherwise must be exact equal.
 */
function matchField(ruleValue: string | null | undefined, inputValue: string | null | undefined): boolean {
  const rv = (ruleValue ?? '').toString().trim();
  const iv = (inputValue ?? '').toString().trim();
  return isWildcard(ruleValue) || rv.toUpperCase() === iv.toUpperCase();
}

/**
 * Check if entire rule matches input across all fields.
 */
function ruleMatches(rule: SimRule, input: SimInput): boolean {
  const countryMatch = matchField(rule.country, input.country);
  const deviceMatch = matchField(rule.deviceType, input.deviceType);
  const osMatch = matchField(rule.os, input.os);
  const sourceMatch = matchField(rule.source, input.source ?? '*');
  return countryMatch && deviceMatch && osMatch && sourceMatch;
}

/**
 * Calculate specificity score: count of non-wildcard fields.
 */
function specificity(rule: SimRule): number {
  let score = 0;
  if (!isWildcard(rule.country)) score++;
  if (!isWildcard(rule.deviceType)) score++;
  if (!isWildcard(rule.os)) score++;
  if (!isWildcard(rule.source)) score++;
  return score;
}

/**
 * Determine winning segment using tracker-like semantics:
 * 1. Filter to active + schedule-active matching rules
 * 2. Group by segment key (tuple equality)
 * 3. Sort segments: specificity desc → priority asc
 * 4. Within best segment's pool: sort weight desc → pick first as winner
 * Returns {winner, pool} or null if no rules match.
 */
export function simulate(rules: SimRule[], input: SimInput): SimResult | null {
  // Step 1: filter rules that could potentially match
  const candidateRules = rules.filter((r) => {
    if (!r.isActive) return false;
    if (r.scheduleActive === false) return false;
    if (!ruleMatches(r, input)) return false;
    return true;
  });

  if (candidateRules.length === 0) return null;

  // Step 2: group by segment tuple (country|device|os|source)
  const segmentMap = new Map<string, SimRule[]>();
  for (const r of candidateRules) {
    const key = `${(r.country ?? '*').trim().toUpperCase()}|${(r.deviceType ?? '*').trim()}|${(r.os ?? '*').trim()}|${(r.source ?? '*').trim()}`;
    if (!segmentMap.has(key)) segmentMap.set(key, []);
    segmentMap.get(key)!.push(r);
  }

  // Step 3: compute segment metadata and sort
  const segments = [...segmentMap.entries()].map(([key, rs]) => ({
    key,
    rules: rs,
    label: (() => {
      const parts: string[] = [];
      if (!isWildcard(rs[0]!.country)) parts.push(`country=${rs[0]!.country?.toUpperCase()}`);
      if (!isWildcard(rs[0]!.deviceType)) parts.push(`device=${rs[0]!.deviceType}`);
      if (!isWildcard(rs[0]!.os)) parts.push(`os=${rs[0]!.os}`);
      if (!isWildcard(rs[0]!.source)) parts.push(`source=${rs[0]!.source}`);
      return parts.join(' · ');
    })(),
    specificity: Math.max(...rs.map(specificity)),
    priority: Math.min(...rs.map((r) => r.priority)),
  })).sort((a, b) => {
    // Primary: specificity descending (most-specific first)
    if (b.specificity !== a.specificity) return b.specificity - a.specificity;
    // Tie-breaker: priority ascending (lower priority evaluated first)
    return a.priority - b.priority;
  });

  // Step 4: take best segment
  const best = segments[0]!;

  // Step 5: within best segment, sort by weight desc → winner = highest weight
  const pool = [...best.rules].sort((a, b) => b.weight - a.weight);
  const winner = pool[0]!;

  return { winner, pool };
}

/**
 * Extract unique countries from rules for dropdown population.
 */
export function getUniqueCountries(rules: SimRule[]): string[] {
  const set = new Set<string>();
  for (const r of rules) {
    const v = r.country?.trim();
    if (v && !isWildcard(v)) set.add(v.toUpperCase());
  }
  // Add common defaults
  ['*', 'US', 'ID', 'CN', 'RU', 'BR'].forEach((c) => set.add(c));
  return Array.from(set).sort();
}

/**
 * Extract unique device types from rules for dropdown population.
 */
export function getUniqueDeviceTypes(rules: SimRule[]): string[] {
  const set = new Set<string>(['*']);
  for (const r of rules) {
    const v = r.deviceType?.trim();
    if (v && !isWildcard(v)) set.add(v.toLowerCase());
  }
  // Ensure standard options
  ['*', 'mobile', 'desktop', 'tablet'].forEach((d) => set.add(d.toLowerCase()));
  return Array.from(set).sort();
}

/**
 * Extract unique OSes from rules for dropdown population.
 */
export function getUniqueOSes(rules: SimRule[]): string[] {
  const set = new Set<string>(['*']);
  for (const r of rules) {
    const v = r.os?.trim();
    if (v && !isWildcard(v)) set.add(v.toLowerCase());
  }
  // Ensure standard options
  ['*', 'ios', 'android', 'windows', 'macos', 'linux'].forEach((o) => set.add(o.toLowerCase()));
  return Array.from(set).sort();
}

/**
 * Is schedule active helper — used by RuleScheduleCell.
 * Active if: schedule===null (24/7) OR (today bit set AND current hour within range using UTC).
 */
export function isScheduleActive(schedule: { tz: string; daysMask: number; startHour: number; endHour: number } | null | undefined, nowMs?: number): boolean {
  if (schedule == null) return true; // 24/7
  const now = nowMs ?? Date.now();
  const date = new Date(now);
  const dayOfWeek = date.getUTCDay(); // 0=Sun..6=Sat → convert to bits 1..7
  const dayBit = 1 << (dayOfWeek + 1);
  if (!(schedule.daysMask & dayBit)) return false;
  const currentHourUTC = date.getUTCHours();
  // Normalize hours: if endHour <= startHour means wraps midnight
  if (schedule.startHour < schedule.endHour) {
    return currentHourUTC >= schedule.startHour && currentHourUTC < schedule.endHour;
  } else {
    // e.g., 22-6 means [22,23] U [0,1,..,5]
    return currentHourUTC >= schedule.startHour || currentHourUTC < schedule.endHour;
  }
}

/**
 * Parse schedule from backend format to our internal shape.
 * Backend sends: { tz, daysMask, startHour, endHour } or null
 */
export function parseSchedule(raw?: any): { tz: string; daysMask: number; startHour: number; endHour: number } | null {
  if (raw == null) return null;
  return {
    tz: String(raw.tz ?? 'UTC'),
    daysMask: Number(raw.daysMask ?? 127),
    startHour: Number(raw.startHour ?? 0),
    endHour: Number(raw.endHour ?? 24),
  };
}

/**
 * Format schedule back to backend-compatible object.
 */
export function formatSchedule(schedule: { tz: string; daysMask: number; startHour: number; endHour: number }): Record<string, any> {
  return {
    tz: schedule.tz,
    daysMask: schedule.daysMask,
    startHour: schedule.startHour,
    endHour: schedule.endHour,
  };
}
